// A5 — Bot admin adapter (module-local, `adapters.ts` ga tegmaydi — C-sessiya
// bilan ziddiyatni kamaytirish uchun). Backend: GET/POST /admin/bots/:id
// (superadmin-gated) — `handlers/admin.go` GetBot/EditBot, `services/admin.go`
// GetBotConfig/EditBot.
import { api } from '@/lib/api'

export interface BotCommand {
  command: string
  description: string
}

/** GET /admin/bots/:id → to'liq bot config (identity + gateway sozlamalari). */
export interface AdminBotConfig {
  id: number
  username: string | null
  first_name: string | null
  is_official: boolean
  /** bot_platform.bots gateway satri bo'lsa — o'chirilgan (revoked_at) holati. */
  revoked?: boolean
  description?: string | null
  about?: string | null
  commands: BotCommand[]
}

/** POST /admin/bots/:id tanasi — barcha maydon ixtiyoriy (faqat berilgani yangilanadi). */
export interface EditBotBody {
  first_name?: string
  is_official?: boolean
  revoked?: boolean
  description?: string
  about?: string
  commands?: BotCommand[]
}

export async function fetchBotConfig(id: number, signal?: AbortSignal): Promise<AdminBotConfig> {
  const r = await api.get<{ bot: AdminBotConfig }>(
    `/admin/bots/${id}`,
    signal ? { signal } : undefined,
  )
  return r.bot
}

export function editBot(id: number, body: EditBotBody): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/bots/${id}`, body)
}

// ── A5.03/06/16 — bot gateway read-endpoints (bot_platform.*) ─────────────────
// Backend: GET /admin/bots/:id/{owner,webhook,apps} (`handlers/admin.go`
// GetBotOwner/GetBotWebhook/GetBotApps). Deploy = lane A — deploy'gacha bu
// yo'llar 404 qaytaradi (UI seksiyalari «kutilmoqda»ga tushadi).

/** A5.03 — bot egasi. */
export interface BotOwner {
  id: number
  username: string | null
  first_name: string | null
  created_at: number | null
  last_seen: number | null
}

/** A5.06 — webhook config + yetkazish sog'ligi (secret_token QAYTARILMAYDI). */
export interface BotWebhook {
  url: string
  ip_address: string | null
  max_connections: number
  pending_count: number
  last_error_at: number | null
  last_error_message: string | null
  next_retry_at: number | null
}

/** A5.16 — Mini App. */
export interface BotApp {
  id: number
  short_name: string
  title: string
  description: string | null
  url: string
  request_write_access: boolean
  has_settings: boolean
  created_at: number | null
}

const opt = (signal?: AbortSignal) => (signal ? { signal } : undefined)

export async function fetchBotOwner(id: number, signal?: AbortSignal): Promise<BotOwner | null> {
  return (await api.get<{ owner: BotOwner | null }>(`/admin/bots/${id}/owner`, opt(signal))).owner
}

export async function fetchBotWebhook(id: number, signal?: AbortSignal): Promise<BotWebhook | null> {
  return (await api.get<{ webhook: BotWebhook | null }>(`/admin/bots/${id}/webhook`, opt(signal))).webhook
}

export async function fetchBotApps(id: number, signal?: AbortSignal): Promise<BotApp[]> {
  return (await api.get<{ apps: BotApp[] }>(`/admin/bots/${id}/apps`, opt(signal))).apps
}

// ── A5.28/29/26-27 — business / affiliate / games ────────────────────────────

/** A5.28 — business ulanish. */
export interface BotBusinessConn {
  id: string
  user_id: number
  enabled: boolean
  created_at: number | null
  username: string | null
  first_name: string | null
}

/** A5.29 — affiliate dastur (commission_permille ÷10 = foiz). */
export interface BotAffiliate {
  commission_permille: number
  duration_months: number | null
  enabled: boolean
  created_at: number | null
}

/** A5.26/27 — o'yin + o'yinchi soni. */
export interface BotGame {
  id: number
  short_name: string
  title: string
  url: string | null
  created_at: number | null
  players: number
}

export async function fetchBotBusiness(id: number, signal?: AbortSignal): Promise<BotBusinessConn[]> {
  return (await api.get<{ connections: BotBusinessConn[] }>(`/admin/bots/${id}/business`, opt(signal))).connections
}

export async function fetchBotAffiliate(id: number, signal?: AbortSignal): Promise<BotAffiliate | null> {
  return (await api.get<{ affiliate: BotAffiliate | null }>(`/admin/bots/${id}/affiliate`, opt(signal))).affiliate
}

export async function fetchBotGames(id: number, signal?: AbortSignal): Promise<BotGame[]> {
  return (await api.get<{ games: BotGame[] }>(`/admin/bots/${id}/games`, opt(signal))).games
}

/** A5.04/05 — xabar-faollik + update-usage. */
export interface BotStats {
  messages_total: number
  messages_7d: number
  messages_today: number
  updates_total?: number
  updates_7d?: number
}

export async function fetchBotStats(id: number, signal?: AbortSignal): Promise<BotStats> {
  return (await api.get<{ stats: BotStats }>(`/admin/bots/${id}/stats`, opt(signal))).stats
}

/** A5.17 — Mini App tema sozlamalari (ranglar int-ARGB). */
export interface BotAppSettings {
  placeholder_path: string | null
  background_color: number | null
  background_dark_color: number | null
  header_color: number | null
  header_dark_color: number | null
  created_at: number | null
}

export async function fetchBotAppSettings(id: number, signal?: AbortSignal): Promise<BotAppSettings | null> {
  return (await api.get<{ settings: BotAppSettings | null }>(`/admin/bots/${id}/app-settings`, opt(signal))).settings
}

/** A5.20 — bot store-listing preview media (skrinshot/video). */
export interface BotPreviewMedia {
  id: number
  lang_code: string
  media_type: string
  position: number
  url: string | null
  thumb_url: string | null
  date: number | null
}

export async function fetchBotPreviewMedia(id: number, signal?: AbortSignal): Promise<BotPreviewMedia[]> {
  return (await api.get<{ media: BotPreviewMedia[] }>(`/admin/bots/${id}/preview-media`, opt(signal))).media
}

/** A5.30 — bot token XAVFSIZLIK metama'lumoti (faqat prefix; sekret QAYTARILMAYDI). */
export interface BotTokenInfo {
  token_prefix: string
  revoked: boolean
  revoked_at: number | null
  issued_at: number | null
  rotated_at: number | null
}

export async function fetchBotToken(id: number, signal?: AbortSignal): Promise<BotTokenInfo | null> {
  return (await api.get<{ token: BotTokenInfo | null }>(`/admin/bots/${id}/token`, opt(signal))).token
}
